import {
  constBoolean,
  constFunction,
  constString,
  ListType,
  opAnd,
  opArtifactAliasAlias,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactMembershipArtifactAliases,
  opArtifactMembershipArtifactVersion,
  opArtifactMemberships,
  opArtifactMembershipVersionIndex,
  opArtifactName,
  opArtifactTypeArtifacts,
  opArtifactTypeName,
  opArtifactVersionId,
  opArtifactVersionTTLDurationSeconds,
  opBooleanEqual,
  opDict,
  opEntityProjects,
  opFilter,
  opFlatten,
  opMap,
  opProjectArtifactType,
  opProjectArtifactTypes,
  opProjectInternalId,
  opProjectName,
  opRootEntity,
  opRootProject,
  opStringIn,
  opStringLower,
  opStringNotEqual,
  OutputNode,
} from '@wandb/weave/core';

import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../util/artifacts';

/**
 * Function to get list of projects under a single entity
 * @param entityName
 * @param defaultProjectName
 */
// TODO: EntityProjects Op doesn't necessarily return all projects - cuts off at 100 projects!!
export function getEntityProjects(
  entityName: string,
  defaultProjectName: string
) {
  // Filter project list to exclude default project
  const projects = opFilter({
    arr: opEntityProjects({
      entity: opRootEntity({
        entityName: constString(entityName),
      }),
    }),
    filterFn: constFunction({row: 'project'}, ({row}) => {
      return opAnd({
        lhs: opStringNotEqual({
          lhs: constString(defaultProjectName ?? ''),
          rhs: opProjectName({
            project: row,
          }),
        } as any),
        rhs: opStringNotEqual({
          lhs: constString(OLD_MODEL_REGISTRY_PROJECT_NAME),
          rhs: opProjectName({
            project: row,
          }),
        } as any),
      });
    }),
  });

  return opDict({
    projects: opMap({
      arr: projects,
      mapFn: constFunction({row: 'project'}, ({row}) => {
        return opDict({
          name: opProjectName({
            project: row,
          }),
          id: opProjectInternalId({
            project: row,
          }),
        } as any);
      }) as any,
    }),
  } as any) as OutputNode<{
    type: 'typedDict';
    propertyTypes: {
      projects: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            name: 'string';
            id: 'string';
          };
        };
      };
    };
  }>;
}

/**
 * Function to get artifact sequences of a given type within a project
 * @param entityName
 * @param projectName
 * @param artifactTypeName
 */
export function getProjectArtifactSequences(
  entityName: string,
  projectName: string,
  artifactTypeName: string
) {
  const artifacts = opFlatten({
    arr: opArtifactTypeArtifacts({
      artifactType: opProjectArtifactType({
        project: opRootProject({
          entityName: constString(entityName),
          projectName: constString(projectName),
        }),
        artifactType: constString(artifactTypeName),
      }),
    }) as any,
  });
  const artifactSequences = opFilter({
    arr: artifacts,
    filterFn: constFunction({row: 'artifact'}, ({row}) => {
      return opBooleanEqual({
        lhs: opArtifactIsPortfolio({
          artifact: row,
        }),
        rhs: constBoolean(false),
      });
    }),
  });
  return opDict({
    artifactSequences: opMap({
      arr: artifactSequences,
      mapFn: constFunction({row: 'artifact'}, ({row}) => {
        return opDict({
          name: opArtifactName({
            artifact: row,
          }),
          id: opArtifactId({
            artifact: row,
          }),
          versions: opMap({
            arr: opArtifactMemberships({
              artifact: row,
            }),
            mapFn: constFunction(
              {row: 'artifactMembership'},
              ({row: rowInner}) => {
                return opDict({
                  versionIndex: opArtifactMembershipVersionIndex({
                    artifactMembership: rowInner,
                  }),
                  aliases: opArtifactAliasAlias({
                    artifactAlias: opArtifactMembershipArtifactAliases({
                      artifactMembership: rowInner,
                    }),
                  }),
                  versionId: opArtifactVersionId({
                    artifactVersion: opArtifactMembershipArtifactVersion({
                      artifactMembership: rowInner,
                    }),
                  }),
                  ttlDurationSeconds: opArtifactVersionTTLDurationSeconds({
                    artifactVersion: opArtifactMembershipArtifactVersion({
                      artifactMembership: rowInner,
                    }),
                  }),
                } as any) as any;
              }
            ),
          }),
        } as any);
      }) as any,
    }),
  } as any) as OutputNode<{
    type: 'typedDict';
    propertyTypes: {
      artifactSequences: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            name: 'string';
            id: 'string';
            versions: {
              type: 'list';
              objectType: {
                type: 'typedDict';
                propertyTypes: {
                  versionIndex: 'number';
                  aliases: {
                    type: 'list';
                    objectType: 'string';
                  };
                  versionId: 'string';
                  ttlDurationSeconds: 'number';
                };
              };
            };
          };
        };
      };
    };
  }>;
}

export function getProjectArtifactTypesNode(
  artifactTypeName: string,
  entityName: string,
  projectName: string
): OutputNode<ListType<'any'>> {
  // Exclude the default project while checking for logged artifacts
  const projectNode = opRootProject({
    entityName: constString(entityName),
    projectName: constString(projectName),
  });

  const artifactTypesNode = opFilter({
    arr: opProjectArtifactTypes({
      project: projectNode as any,
    }),
    filterFn: constFunction({row: 'artifactType'}, ({row}) => {
      return opStringIn({
        lhs: opStringLower({
          str: constString(artifactTypeName),
        }),
        rhs: opStringLower({
          str: opArtifactTypeName({
            artifactType: row,
          }),
        }),
      } as any);
    }),
  });

  return artifactTypesNode as OutputNode<ListType<'any'>>;
}
